<script lang="ts">
	import { cn } from 'tailwind-variants'

	import InfoPopover from '#lib/components/info-popover.svelte'
	import type { ChainId } from '#lib/models/index.js'

	let {
		class: className,
		leftValue,
		rightValue,
		prettyLeftValue,
		prettyRightValue,
		left,
		right,
		leftPopover,
		rightPopover,
		ratio,
		leftRelation,
		rightRelation,
	}: {
		class?: Nullable<string>
		leftValue?: Nullable<number>
		rightValue?: Nullable<number>
		prettyLeftValue: string
		prettyRightValue: string
		left: { id: ChainId; name: string; slug: string }
		right: { id: ChainId; name: string; slug: string }
		leftPopover?: Nullable<{
			content: Nullable<string>
			ariaLabel: Nullable<string>
			type?: 'info' | 'warning'
		}>
		rightPopover?: Nullable<{
			content: Nullable<string>
			ariaLabel: Nullable<string>
			type?: 'info' | 'warning'
		}>
		ratio?: Nullable<number>
		leftRelation?: Nullable<number>
		rightRelation?: Nullable<number>
	} = $props()
</script>

<div
	class={cn(
		'mt-auto grid w-full grid-rows-2 items-center justify-center gap-x-3 gap-y-4',
		leftRelation != null && rightRelation != null
			? 'grid-cols-[24px_1fr_auto]'
			: 'grid-cols-[24px_1fr]',
		className
	)}
>
	<img
		class="size-6"
		alt="{left.name} logo"
		crossorigin="anonymous"
		decoding="async"
		fetchpriority="low"
		height="24"
		loading="lazy"
		src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{left.slug}.svg"
		width="24"
	/>

	{#if leftRelation != null && rightRelation != null}
		<div class="bg-muted h-2.5 w-full rounded-full">
			<!-- eslint-disable svelte/no-inline-styles -->
			<div
				style:width="{leftRelation}%"
				class="h-2.5 rounded-full"
				class:bg-gray-300={ratio === 1}
				class:bg-green-500={ratio != null && leftValue != null ? ratio > 1 : false}
				class:bg-red-400={ratio != null && leftValue != null ? ratio < 1 : false}
				class:min-w-2.5={leftRelation !== 0}
			></div>
		</div>
	{/if}

	<div
		class="flex items-center gap-1"
		class:justify-end={leftRelation != null && !!leftPopover?.content}
		class:justify-start={leftRelation != null && !leftPopover?.content}
	>
		<span
			class="whitespace-nowrap"
			class:text-green-500={ratio != null && leftValue != null ? ratio > 1 : false}
			class:text-red-400={ratio != null && leftValue != null ? ratio < 1 : false}
		>
			{prettyLeftValue}
		</span>

		<InfoPopover {...leftPopover} />
	</div>

	<img
		class="size-6"
		alt="{right.name} logo"
		crossorigin="anonymous"
		decoding="async"
		fetchpriority="low"
		height="24"
		loading="lazy"
		src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{right.slug}.svg"
		width="24"
	/>

	{#if leftRelation != null && rightRelation != null}
		<div class="bg-muted h-2.5 w-full rounded-full">
			<!-- eslint-disable svelte/no-inline-styles -->
			<div
				style:width="{rightRelation}%"
				class="h-2.5 rounded-full"
				class:bg-gray-300={ratio === 1}
				class:bg-green-500={ratio != null && rightValue != null ? ratio < 1 : false}
				class:bg-red-400={ratio != null && rightValue != null ? ratio > 1 : false}
				class:min-w-2.5={rightRelation !== 0}
			></div>
		</div>
	{/if}

	<div
		class="flex items-center gap-1"
		class:justify-end={rightRelation != null && !!rightPopover?.content}
		class:justify-start={rightRelation != null && !rightPopover?.content}
	>
		<span
			class="whitespace-nowrap"
			class:text-green-500={ratio != null && rightValue != null ? ratio < 1 : false}
			class:text-red-400={ratio != null && rightValue != null ? ratio > 1 : false}
		>
			{prettyRightValue}
		</span>

		<InfoPopover {...rightPopover} />
	</div>
</div>
